import { createApi } from '@reduxjs/toolkit/query/react';
import { toast } from 'sonner';
import type {
  TAnyClientQuestionType,
  TAvailableLanguageCodes,
  SuggestTemplateQuestionsWithIdResponse,
} from '@hubertai/screening-question-types';
import type { TPaginationResult } from '../api-common.types';
import type { TPaginationQuestionsRequest, TQuestionExistsInTemplate } from './questions.api.types';
import PrettyPrintJson from 'components/common/PrettyPrintJson';
import config from 'config/index';
import { addToLibrary } from 'rtk/LibrarySlice';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import { EUserType } from 'types/accessControll.types';
import type { TQuestionConfigDTO } from 'types/questions/question.types.v4';
import type { SuggestTemplateQuestionsPayload } from 'components/AgentChat/chat/types/templateWithSuggestedQuestions.types';
import type {
  SuggestedQuestion,
  SuggestTemplateQuestionsWithIdClientResponse,
} from 'rtk/query/apis/templates/templates.api.types';
import { tryGetErrorMessage } from 'utils/tryGetErrorMessage';
import { toastRuleReferenceBlockedError } from 'utils/toastRuleReferenceBlockedError';
import type { TShareWithAccessGroupsResult } from 'types/template/templateAudience.types';

const reducerPath = 'questions' as const;
const path = 'v1/questions' as const;

/**
 * Set by the backend when Gemini has no capacity left (HTTP 429/503 from the model). Kept in sync
 * with `AI_MODEL_BUSY_CODE` in hubert-internal-manager-backend's suggest-questions.gemini.ts.
 */
const AI_MODEL_BUSY_CODE = 'AI_MODEL_BUSY';

/** True when a failed request failed only because the AI model was out of capacity, i.e. retrying later works. */
export function isAiModelBusyError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return false;
  return (data as { code?: unknown }).code === AI_MODEL_BUSY_CODE;
}

export const questionsApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  tagTypes: ['QuestionContext', 'QuestionSearch'],
  endpoints: (builder) => ({
    getQuestionById: builder.query<
      {
        question: TAnyClientQuestionType;
        questionV4: TAnyClientQuestionType;
      },
      { questionId: string | null }
    >({
      query: (payload) => ({
        url: `/${payload.questionId}`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }: any) {
        try {
          const questionId = payload.questionId;
          const data = await queryFulfilled;
          const questionV4 = JSON.parse(JSON.stringify(data.data.questionV4));
          const state = getState();
          if (questionId) {
            const library = state.Library;
            if (!(questionId in library)) {
              dispatch(addToLibrary({ question: { ...questionV4 } }));
            }
          }
        } catch (error) {
          console.log('ERROR: getQuestionById onQueryStarted: ', error);
        }
      },
    }),

    searchQuestions: builder.mutation<any, any>({
      query: (searchParams: {
        queryString: string;
        text?: string;
        language?: string;
        tags?: string;
        role?: string;
        type?: string;
        clientId?: string;
        global: boolean;
      }) => {
        // let searchParams = {};
        // if (text.length) search_params.text = text;
        // if (language.length) search_params.language = language;
        // if (tags.length) search_params.tags = tags;
        // if (role.length) search_params.role = role;
        // if (types.length) search_params.types = types;
        console.log('searchQuestions query: queryString', searchParams);
        return {
          url: `/search?${searchParams.queryString}`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }: any) {
        try {
          // const state = getState();
          const data = await queryFulfilled;
          const questions = data.data.questions;
          console.log('searchQuestions onQueryStarted: queryFulfilled', questions);
          const state = getState();
          const Library = state.Library;

          questions.map((question: any) => {
            if (!(question.id in Library)) {
              dispatch(addToLibrary({ question }));
            }
          });
          // console.log('getQuestionById queryFulfilled', question);
        } catch (error) {
          console.log('ERROR: getQuestionById onQueryStarted: ', error);
        }
      },
    }),

    createQuestion: builder.mutation<{ question: TAnyClientQuestionType }, TAnyClientQuestionType>({
      query: (question) => {
        console.log('createQuestion mutation', question);
        return {
          url: `/create`,
          method: 'POST',
          data: {
            question,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      async onQueryStarted(
        _,
        {
          dispatch,
          queryFulfilled,
          // getState
        }: any,
      ) {
        try {
          // const state = getState();
          const data = await queryFulfilled;
          const question = data.data.question;
          console.log('searchQuestions onQueryStarted: queryFulfilled', question);

          if (question) {
            dispatch(addToLibrary({ question }));
          }
          // console.log('getQuestionById queryFulfilled', question);
        } catch (error) {
          console.log('ERROR: getQuestionById onQueryStarted: ', error);
        }
      },
    }),

    updateQuestion: builder.mutation<{ question: TAnyClientQuestionType }, TAnyClientQuestionType>({
      query: (question) => {
        console.log('createQuestion mutation', question);
        return {
          url: `/update`,
          method: 'POST',
          data: {
            question,
          },
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      // Without this, getQuestionContext/getQuestionContextByObjectId keep serving their
      // pre-update snapshot for this id, so a caller reporting success still sees stale
      // data.questionV4 (and anything reseeded from it) until an unrelated cache eviction occurs.
      invalidatesTags: (_, __, question) => [{ type: 'QuestionContext', id: question.id }],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }: any) {
        try {
          const data = await queryFulfilled;
          const question = data.data.question;
          console.log('updateQuestion onQueryStarted data', data);

          if (question) {
            // Keep the language the question is being viewed in; the default is its first language.
            const viewing = getState().Library?.[question.id]?.language?.c;
            const cLanguage = question.available_languages.includes(viewing) ? viewing : undefined;
            dispatch(addToLibrary({ question, cLanguage }));
            toast.success(`Question (${question.id}) succesfully saved.`);
          }
        } catch (error) {
          if (toastRuleReferenceBlockedError(error, getState().Library)) return;

          const errorMessage = tryGetErrorMessage(error);
          if (errorMessage) {
            toast.error(errorMessage);
            return;
          }

          toast.error(`asdlkmalskdlkasdm Something went wrong. ${error}`);
          console.log('ERROR: getQuestionById onQueryStarted: ', error);
        }
      },
    }),

    shareWithAccessGroups: builder.mutation<
      { results: TShareWithAccessGroupsResult[] },
      { questionIds: string[]; accessGroupIds: string[] }
    >({
      query: (payload) => ({
        url: `/share-with-access-groups`,
        method: 'POST',
        data: payload,
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
      invalidatesTags: (_, __, payload) => [
        ...payload.questionIds.map((id) => ({ type: 'QuestionContext' as const, id })),
        'QuestionSearch',
      ],
    }),

    addQuestionLanguage: builder.mutation<
      { question: any },
      {
        questionId: string;
        languageFrom: string;
        languageTo: string;
        autoTranslate: boolean;
      }
    >({
      query: (x) => {
        console.log('copyQuestion mutation', x);
        return {
          url: `/${x.questionId}/add-language`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      invalidatesTags: (_, __, x) => [{ type: 'QuestionContext', id: x.questionId }],
      async onQueryStarted(x, { dispatch, queryFulfilled /*getState*/ }: any) {
        try {
          const data = await queryFulfilled;
          const question = data.data.question;

          if (question) {
            dispatch(addToLibrary({ question, cLanguage: x.languageTo as TAvailableLanguageCodes }));
          }
        } catch (error) {
          console.log('ERROR: addQuestionLanguage onQueryStarted: ', error);
        }
      },
    }),

    findPaginatedQuestions: builder.query<TPaginationResult<TAnyClientQuestionType>, TPaginationQuestionsRequest>({
      query: (payload) => {
        return {
          url: `/paginated-find`,
          method: 'POST',
          data: payload,
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      providesTags: ['QuestionSearch'],
    }),

    copyQuestionsToTestDB: builder.mutation<any, void>({
      query: () => {
        return {
          url: `/copy/questions-to-test-db`,
          method: 'GET',
          headers: {
            'X-Access-Control': EUserType.HUBERT,
          },
        };
      },
      async onQueryStarted(payload: any, { /*dispatch, */ queryFulfilled /* getState */ }: any) {
        try {
          console.log('copyQuestionsToTestDB onQueryStarted', payload);
          const data = await queryFulfilled;
          const str = PrettyPrintJson({ data: data.data.results });
          toast.success(str);
          console.log('copyQuestionsToTestDB queryFulfilled data', data);
        } catch (error) {
          toast.error(`Something went wrong. ${error}`);
          console.log('ERROR: copyQuestionsToTestDB: ', error);
        }
      },
    }),

    getTemplatesUsingQuestion: builder.query<{ templates: TQuestionExistsInTemplate[] }, { questionId: string }>({
      query: ({ questionId }) => ({
        url: `/${questionId}/templates`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    isQuestionPublishedOrNonExclusive: builder.query<
      { isPublishedOrNonExclusiveQuestion: boolean },
      { questionId?: string }
    >({
      query: ({ questionId }) => {
        return {
          url: `/${questionId}/published-or-non-exclusive`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
    }),

    // Read-only preview translation — never persists, just returns the question translated into
    // `languageTo` so a question missing that language can still be diffed/previewed. A `query`
    // (not mutation) deliberately, so RTK Query caches/dedupes repeat calls for the same question+language.
    previewQuestionTranslation: builder.query<
      { question: TAnyClientQuestionType },
      { questionId: string; languageTo: TAvailableLanguageCodes }
    >({
      query: ({ questionId, languageTo }) => ({
        url: `/${questionId}/preview-translation`,
        method: 'POST',
        data: { languageTo },
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    getQuestionsByIds: builder.query<{ questions: TAnyClientQuestionType[] }, { questionIds: string[] }>({
      query: ({ questionIds }) => ({
        url: `/by-ids`,
        method: 'POST',
        data: { questionIds },
        headers: { 'X-Access-Control': EUserType.ANY },
      }),
    }),

    getQuestionContext: builder.query<TQuestionConfigDTO, { questionId?: string; templateId?: string }>({
      query: ({ questionId, templateId }) => {
        return {
          url: templateId ? `/${questionId}/${templateId}/question-config` : `/${questionId}/question-config`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      providesTags: (_, __, { questionId }) => [{ type: 'QuestionContext', id: questionId }],
    }),

    // A question's own source_question_id (set when it was copied from another question — see
    // translateSuggestedQuestions) is an ObjectId, not a nanoid id — getQuestionContext only ever
    // looks up by nanoid id. Use this instead when fetching a question by that ObjectId, e.g. to
    // compare a copy against its source.
    getQuestionContextByObjectId: builder.query<TQuestionConfigDTO, { objectId?: string }>({
      query: ({ objectId }) => {
        return {
          url: `/by-object-id/${objectId}/question-config`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.ANY },
        };
      },
      providesTags: (_, __, { objectId }) => [{ type: 'QuestionContext', id: objectId }],
    }),
    getSuggestedQuestions: builder.query<SuggestTemplateQuestionsWithIdClientResponse, SuggestTemplateQuestionsPayload>(
      {
        query: (payload) => {
          return {
            url: `/suggest`,
            method: 'POST',
            data: { body: { ...payload } },
            headers: { 'X-Access-Control': EUserType.ANY },
          };
        },
        transformResponse: (raw: unknown): SuggestTemplateQuestionsWithIdClientResponse => {
          const response = raw as SuggestTemplateQuestionsWithIdResponse;
          if (!('add' in response)) return response as SuggestTemplateQuestionsWithIdClientResponse;
          return {
            ...response,
            add: response.add.map((item) => ({
              ...item,
              suggested_question: item.suggested_question as SuggestedQuestion | null,
            })),
          };
        },
      },
    ),
  }),
});

export const {
  // Queries
  useGetQuestionByIdQuery,
  useFindPaginatedQuestionsQuery,
  useIsQuestionPublishedOrNonExclusiveQuery,
  usePreviewQuestionTranslationQuery,
  useGetQuestionsByIdsQuery,
  useGetQuestionContextQuery,
  useGetQuestionContextByObjectIdQuery,
  useGetSuggestedQuestionsQuery,
  // Mutations
  useUpdateQuestionMutation,
  useShareWithAccessGroupsMutation,
  useCreateQuestionMutation,
  useAddQuestionLanguageMutation,
  useCopyQuestionsToTestDBMutation,
  useGetTemplatesUsingQuestionQuery,
} = questionsApi;
